"use client"

import { useCallback, useEffect, useRef, useState } from "react"

import { LoginTurnstileError, LoginTurnstileWidget } from "./turnstile-widget"

export { LoginTurnstileError, readyLoginTurnstile, type LoginTurnstileFailure } from "./turnstile-widget"

type LoginTurnstileCallbacks = {
  isAttemptActive: () => boolean
}

export function useLoginTurnstile({
  enabled,
  siteKey,
  locale,
  callbacks,
}: {
  enabled: boolean
  siteKey: string
  locale: string
  callbacks: LoginTurnstileCallbacks
}) {
  const callbackRef = useRef(callbacks)
  const widgetRef = useRef<LoginTurnstileWidget | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isChallengeVisible, setChallengeVisible] = useState(false)

  useEffect(() => {
    callbackRef.current = callbacks
  })

  useEffect(() => {
    if (!enabled) return
    // A fresh session per effect run keeps page-load preparation across StrictMode's simulated remount.
    const widget = new LoginTurnstileWidget({
      siteKey,
      locale,
      getContainer: () => containerRef.current,
      isAttemptActive: () => callbackRef.current.isAttemptActive(),
      focusChallenge: () => focusChallengeFrame(containerRef.current),
      setChallengeVisible,
    })
    widgetRef.current = widget
    widget.start()
    return () => {
      widget.dispose()
      if (widgetRef.current === widget) widgetRef.current = null
    }
  }, [enabled, locale, siteKey])

  const getToken = useCallback(async (): Promise<string> => {
    const widget = widgetRef.current
    if (!widget) throw new LoginTurnstileError("unavailable")
    return widget.getToken()
  }, [])

  const reset = useCallback(() => {
    widgetRef.current?.reset()
  }, [])

  return {
    challengeContainerRef: containerRef,
    getToken,
    isChallengeVisible,
    reset,
  }
}

function focusChallengeFrame(container: HTMLElement | null) {
  if (!container) return
  requestAnimationFrame(() => {
    const frame = container.querySelector("iframe")
    if (frame instanceof HTMLElement) {
      frame.focus({ preventScroll: true })
    } else {
      container.focus({ preventScroll: true })
    }
  })
}
